/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Tooltip } from "antd";
import {
  InfoCircleOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InboxOutlined,
  SwapOutlined,
  CarOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import "./index.scss";

// ─── Card configuration ────────────────────────────────────────
const CARD_CONFIG = [
  {
    match: "Total Available",
    role: "total",
    shortLabel: "Total Available",
    description: "All shipments currently tracked at this hub",
    icon: <InboxOutlined />,
    color: "#1a73e8",
    accent: "#e8f0fe",
  },
  {
    match: "Inbound Pendency",
    role: "pendency",
    shortLabel: "Inbound Pendency",
    description: "Pending Inscan",
    icon: <ExclamationCircleOutlined />,
    color: "#e53935",
    accent: "#fdecea",
    warnAt: 15,
    criticalAt: 25,
  },
  {
    match: "Outscan Pending Inscan To Hub",
    role: "pendency",
    shortLabel: "Outscan → Hub",
    description: "Outscanned but not inscanned at destination hub",
    icon: <SwapOutlined />,
    color: "#f57c00",
    accent: "#fff3e0",
    warnAt: 10,
    criticalAt: 20,
  },
  {
    match: "Outscan Pending Inscan To Cp",
    role: "pendency",
    shortLabel: "Outscan → CP",
    description: "Outscanned but not inscanned at delivery CP",
    icon: <CarOutlined />,
    color: "#7b1fa2",
    accent: "#f3e5f5",
    warnAt: 5,
    criticalAt: 10,
  },
];

const getConfig = (name) =>
  CARD_CONFIG.find((c) => name.includes(c.match)) || CARD_CONFIG[0];

const getSeverity = (card, totalValue, cfg) => {
  if (cfg.role !== "pendency" || !totalValue || !card.value) return "low";
  const pct = (card.value / totalValue) * 100;
  if (pct >= (cfg.criticalAt || 25)) return "critical";
  if (pct >= (cfg.warnAt || 15)) return "warning";
  return "low";
};

const SEVERITY_META = {
  critical: { label: "Critical", icon: <WarningOutlined />, className: "sev-critical" },
  warning: { label: "Watch", icon: <ExclamationCircleOutlined />, className: "sev-warning" },
  low: { label: "Normal", icon: <CheckCircleOutlined />, className: "sev-low" },
};

// ─── Component ─────────────────────────────────────────────────
const TopCard = ({ cards: initialCards, loading = false, onCardClick }) => {
  const [cards, setCards] = useState(initialCards || []);

  useEffect(() => {
    if (initialCards && Array.isArray(initialCards)) {
      setCards(initialCards);
    }
  }, [initialCards]);

  const totalCard = useMemo(
    () => cards.find((c) => c.name.includes("Total Available")),
    [cards]
  );
  const pendencyCards = useMemo(
    () => cards.filter((c) => !c.name.includes("Total Available")),
    [cards]
  );

  const totalValue = totalCard?.value || 0;

  // Compute overall hub health
  const hubHealth = useMemo(() => {
    if (!totalValue) return null;
    const pendencySum = pendencyCards.reduce((s, c) => s + (c.value || 0), 0);
    const pct = (pendencySum / totalValue) * 100;
    if (pct >= 40) return { level: "critical", label: "Needs Attention", pct };
    if (pct >= 20) return { level: "warning", label: "Monitor Closely", pct };
    return { level: "healthy", label: "Operating Well", pct };
  }, [pendencyCards, totalValue]);

  if (cards.length === 0 && !loading) return null;

  return (
    <div className={`pendency-command-center ${loading ? "is-loading" : ""}`}>

      {/* ── Top band: Total Available + Health ── */}
      <div
        className="hub-summary-band"
        onClick={() => totalCard && onCardClick && onCardClick(totalCard)}
      >
        <div className="hub-summary-left">
          <div className="hub-summary-icon">
            <InboxOutlined />
          </div>
          <div className="hub-summary-text">
            <div className="hub-summary-label">Total Available Shipments</div>
            <div className="hub-summary-desc">
              All shipments currently tracked at this hub
            </div>
          </div>
        </div>

        <div className="hub-summary-center">
          {hubHealth && (
            <div className={`hub-health-pill health-${hubHealth.level}`}>
              {hubHealth.level === "healthy" && <CheckCircleOutlined />}
              {hubHealth.level === "warning" && <ExclamationCircleOutlined />}
              {hubHealth.level === "critical" && <WarningOutlined />}
              <span>{hubHealth.label}</span>
            </div>
          )}
        </div>

        <div className="hub-summary-right">
          <div className="hub-total-value">
            {loading ? "—" : totalValue.toLocaleString()}
          </div>
          <div className="hub-summary-action">
            <ArrowRightOutlined />
          </div>
        </div>
      </div>

      {/* ── Pendency Cards ── */}
      <div className="pendency-cards-section">
        <div className="pendency-section-header">
          <span className="section-label">Pendency Breakdown</span>
        </div>

        <Row gutter={[16, 16]}>
          {pendencyCards.map((card) => {
            const cfg = getConfig(card.name);
            const severity = getSeverity(card, totalValue, cfg);
            const sevMeta = SEVERITY_META[severity];
            const pct =
              totalValue > 0
                ? ((card.value / totalValue) * 100).toFixed(1)
                : "0.0";
            const barWidth = Math.min(parseFloat(pct), 100);

            return (
              <Col key={card.id} xs={24} sm={24} md={8}>
                <div
                  className={`pendency-card ${sevMeta.className}`}
                  onClick={() => onCardClick && onCardClick(card)}
                  style={{ "--card-color": cfg.color, "--card-accent": cfg.accent }}
                >
                  {/* Severity badge */}
                  <div className="pcard-header">
                    <span className={`severity-pill ${sevMeta.className}`}>
                      {sevMeta.icon}
                      <span>{sevMeta.label}</span>
                    </span>
                    {card.statuses?.length > 0 && (
                      <Tooltip title={card.statuses.join(", ")}>
                        <InfoCircleOutlined className="pcard-info" />
                      </Tooltip>
                    )}
                  </div>

                  {/* Value */}
                  <div className="pcard-value">
                    {loading ? "—" : (card.value ?? 0).toLocaleString()}
                  </div>

                  {/* Label + description */}
                  <div className="pcard-label">{cfg.shortLabel}</div>
                  <div className="pcard-desc">{cfg.description}</div>

                  {/* Progress bar (% of total) */}
                  {totalValue > 0 && (
                    <div className="pcard-progress">
                      <div className="pcard-bar-track">
                        <div
                          className="pcard-bar-fill"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="pcard-pct">{pct}% of total</span>
                    </div>
                  )}

                  {/* Drill down */}
                  <div className="pcard-action">
                    <ArrowRightOutlined />
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

TopCard.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      visible: PropTypes.bool,
    })
  ).isRequired,
  onCardClick: PropTypes.func,
  loading: PropTypes.bool,
};

export default TopCard;
